import type { ProfileResponse } from '@/services/user.service';

export type SubscriberProfileRequiredField =
  | 'document'
  | 'cep'
  | 'street'
  | 'number'
  | 'neighborhood'
  | 'city'
  | 'state';

export interface SubscriberProfileCompletionResult {
  requiresCompletion: boolean;
  missingFields: SubscriberProfileRequiredField[];
}

export interface SubscriberProfileCompletionFormData {
  cpf: string;
  cnpj: string;
  companyName: string;
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

const normalizeText = (value?: string | null) => (value || '').trim();

const resolveAddressValue = (
  address: ProfileResponse['address'] | null | undefined,
  ...keys: string[]
) => {
  for (const key of keys) {
    const value = normalizeText(address?.[key as keyof NonNullable<ProfileResponse['address']>] as string | undefined);
    if (value) {
      return value;
    }
  }
  return '';
};

export const subscriberProfileMissingFieldLabels: Record<SubscriberProfileRequiredField, string> = {
  document: 'CPF ou CNPJ',
  cep: 'CEP',
  street: 'logradouro',
  number: 'número',
  neighborhood: 'bairro',
  city: 'cidade',
  state: 'estado',
};

export const mapProfileToSubscriberProfileCompletionForm = (
  profile?: ProfileResponse | null,
): SubscriberProfileCompletionFormData => ({
  cpf: normalizeText(profile?.document),
  cnpj: normalizeText(profile?.cnpj || profile?.company_document),
  companyName: normalizeText(profile?.company_name),
  cep: resolveAddressValue(profile?.address, 'cep', 'zip_code'),
  street: resolveAddressValue(profile?.address, 'street', 'line1'),
  number: resolveAddressValue(profile?.address, 'number'),
  complement: resolveAddressValue(profile?.address, 'complement', 'line2'),
  neighborhood: resolveAddressValue(profile?.address, 'neighborhood'),
  city: resolveAddressValue(profile?.address, 'city'),
  state: resolveAddressValue(profile?.address, 'state'),
});

export const getSubscriberProfileCompletion = (
  profile?: ProfileResponse | null,
): SubscriberProfileCompletionResult => {
  if (!profile || normalizeText(profile.user_type).toLowerCase() !== 'subscriber') {
    return {
      requiresCompletion: false,
      missingFields: [],
    };
  }

  const missingFields: SubscriberProfileRequiredField[] = [];
  const hasDocument = Boolean(
    normalizeText(profile.document) ||
      normalizeText(profile.cnpj) ||
      normalizeText(profile.company_document),
  );
  if (!hasDocument) {
    missingFields.push('document');
  }

  const address = profile.address;
  const addressChecks: Array<[SubscriberProfileRequiredField, string[]]> = [
    ['cep', ['cep', 'zip_code']],
    ['street', ['street', 'line1']],
    ['number', ['number']],
    ['neighborhood', ['neighborhood']],
    ['city', ['city']],
    ['state', ['state']],
  ];

  for (const [fieldName, keys] of addressChecks) {
    if (!resolveAddressValue(address, ...keys)) {
      missingFields.push(fieldName);
    }
  }

  return {
    requiresCompletion: missingFields.length > 0,
    missingFields,
  };
};

/**
 * Decide se a modal obrigatória de cadastro deve ser exibida. Além das
 * pendências de dados cadastrais (CPF/CNPJ, endereço), também dispara quando o
 * backend sinaliza `requires_vio_provisioning` — assinatura ativa de um
 * plano da VIO Pay que ainda não foi provisionada. Rede de segurança para o
 * caso em que o perfil já está completo mas a assinatura nunca chegou à VIO
 * (ex.: falha de provisionamento no signup — caso Ana Carolina): a modal
 * reaparece e o submit re-chama o vio-sync.
 */
export const shouldRequireSubscriberProfileModal = (
  profile?: ProfileResponse | null,
): boolean => {
  if (getSubscriberProfileCompletion(profile).requiresCompletion) {
    return true;
  }
  return Boolean(profile?.requires_vio_provisioning);
};
